// textures.ts — small procedural grayscale detail maps (no image assets).
// The ground shader multiplies its base colour by these, sampled in world
// space, so roads, grass and pavement get texture without UV work.
import * as THREE from "three";

export type DetailPattern = "grass" | "asphalt" | "tiles";

const cache = new Map<DetailPattern, THREE.Texture>();

function noiseCanvas(size: number, seed: number) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  let s = seed;
  const rand = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  return { canvas, ctx, rand };
}

function paint(pattern: DetailPattern): HTMLCanvasElement {
  const size = 256;
  const { canvas, ctx, rand } = noiseCanvas(size, pattern === "grass" ? 7 : pattern === "asphalt" ? 13 : 29);
  ctx.fillStyle = "rgb(128,128,128)";
  ctx.fillRect(0, 0, size, size);
  if (pattern === "grass") {
    // soft patches + many short blades
    for (let i = 0; i < 26; i++) {
      const v = 100 + rand() * 60;
      ctx.fillStyle = `rgba(${v},${v},${v},0.35)`;
      ctx.beginPath();
      ctx.ellipse(rand() * size, rand() * size, 20 + rand() * 40, 14 + rand() * 30, rand() * 3, 0, Math.PI * 2);
      ctx.fill();
    }
    for (let i = 0; i < 2600; i++) {
      const v = rand() < 0.5 ? 70 + rand() * 40 : 170 + rand() * 60;
      ctx.strokeStyle = `rgba(${v},${v},${v},0.55)`;
      ctx.lineWidth = 1;
      const x = rand() * size;
      const y = rand() * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rand() - 0.5) * 3, y - 2 - rand() * 4);
      ctx.stroke();
    }
  } else if (pattern === "asphalt") {
    for (let i = 0; i < 9000; i++) {
      const v = 80 + rand() * 110;
      ctx.fillStyle = `rgba(${v},${v},${v},0.5)`;
      ctx.fillRect(rand() * size, rand() * size, 1 + rand() * 1.5, 1 + rand() * 1.5);
    }
    // a few darker patches / cracks
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = "rgba(60,60,60,0.45)";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      let x = rand() * size;
      let y = rand() * size;
      ctx.moveTo(x, y);
      for (let k = 0; k < 6; k++) {
        x += (rand() - 0.5) * 30;
        y += (rand() - 0.5) * 30;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  } else {
    // paving tiles: 4×4 slabs with joints + per-slab tint
    const tile = size / 4;
    for (let tx = 0; tx < 4; tx++) {
      for (let ty = 0; ty < 4; ty++) {
        const v = 120 + rand() * 30;
        ctx.fillStyle = `rgb(${v},${v},${v})`;
        ctx.fillRect(tx * tile, ty * tile, tile, tile);
      }
    }
    for (let i = 0; i < 3000; i++) {
      const v = 100 + rand() * 80;
      ctx.fillStyle = `rgba(${v},${v},${v},0.35)`;
      ctx.fillRect(rand() * size, rand() * size, 1, 1);
    }
    ctx.fillStyle = "rgb(70,70,70)";
    for (let k = 0; k <= 4; k++) {
      ctx.fillRect(k * tile - 2, 0, 4, size);
      ctx.fillRect(0, k * tile - 2, size, 4);
    }
  }
  return canvas;
}

export function detailTexture(pattern: DetailPattern): THREE.Texture {
  const hit = cache.get(pattern);
  if (hit) return hit;
  const texture = new THREE.CanvasTexture(paint(pattern));
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  cache.set(pattern, texture);
  return texture;
}
